import { z } from 'zod'

const requiredText = (label, max = 200) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(max, `${label} cannot exceed ${max} characters`)

const addressSchema = z.object({
  line1: z.string().trim().max(200).default(''),
  line2: z.string().trim().max(200).default(''),
  city: z
    .string()
    .trim()
    .max(100)
    .default('')
    .refine(
      (val) => !val || /^[a-zA-Z0-9\s.,'()/-]+$/.test(val),
      'City must contain valid characters',
    ),
  state: z
    .string()
    .trim()
    .max(100)
    .default('')
    .refine(
      (val) => !val || /^[a-zA-Z0-9\s.,'()/-]+$/.test(val),
      'State must contain valid characters',
    ),
  pincode: z
    .string()
    .trim()
    .max(20)
    .default('')
    .refine(
      (val) => !val || /^\d+$/.test(val),
      'Pincode must contain only numbers',
    ),
  country: z
    .string()
    .trim()
    .max(100)
    .default('')
    .refine(
      (val) => !val || /^[a-zA-Z\s]+$/.test(val),
      'Country must accept only alphabetic characters',
    ),
})

const cgstSchema = z
  .number({ error: 'CGST must be a number' })
  .min(0, 'CGST must be 0 or more')
  .max(100, 'CGST cannot exceed 100')

const sgstSchema = z
  .number({ error: 'SGST must be a number' })
  .min(0, 'SGST must be 0 or more')
  .max(100, 'SGST cannot exceed 100')

const igstSchema = z
  .number({ error: 'IGST must be a number' })
  .min(0, 'IGST must be 0 or more')
  .max(100, 'IGST cannot exceed 100')

export const quotationProductSchema = z.object({
  product: z
    .string()
    .trim()
    .min(1, 'Product name is required')
    .max(100, 'Product name cannot exceed 100 characters')
    .refine(
      (val) => /^[a-zA-Z0-9\s.,'()-]+$/.test(val),
      'Product name must be alphanumeric',
    ),

  description: z
    .string()
    .trim()
    .refine(
      (val) => !val || val.trim().split(/\s+/).filter(Boolean).length <= 500,
      'Product description cannot exceed 500 words',
    )
    .optional()
    .default(''),

  hsn: z.string().trim().max(50).default(''),

  unitPrice: z
    .number({ error: 'Unit price must be a number' })
    .gt(0, 'Unit price must be greater than 0'),

  quantity: z
    .number({ error: 'Quantity must be a number' })
    .int('Quantity must be a whole number')
    .min(1, 'Quantity must be at least 1'),

  beforeGST: z.number().min(0),

  // Optional here because the required tax fields
  // depend on the quotation taxType.
  cgst: cgstSchema.optional(),

  sgst: sgstSchema.optional(),

  igst: igstSchema.optional(),

  total: z.number().min(0),
})

export const createQuotationFormSchema = z
  .object({
    lead: z.string().trim().min(1, 'Lead ID is missing.'),

    quoteNo: requiredText('Quote number', 50),

    contactName: requiredText('Contact name', 120),

    accountName: requiredText('Account name', 150),

    quoteDate: z
      .string()
      .trim()
      .min(1, 'Quote date is required')
      .refine(
        (value) => !Number.isNaN(Date.parse(value)),
        'Invalid quote date',
      ),

    validUntil: z
      .union([z.string(), z.null()])
      .optional()
      .refine((value) => {
        if (!value) return true

        return !Number.isNaN(Date.parse(value))
      }, 'Invalid valid until date'),

    billingAddress: addressSchema,

    shippingAddress: addressSchema,

    taxType: z
      .enum([
        'cgst_sgst',
        'igst',
        'export_lut_igst',
        'export_lut_igst_sgst_cgst',
      ])
      .default('cgst_sgst'),

    products: z
      .array(quotationProductSchema)
      .min(1, 'At least one product is required'),

    subTotal: z.number().min(0),

    gstAmount: z.number().min(0),

    grandTotal: z.number().min(0),

    termsAndConditions: z.object({
      warranty: requiredText('Warranty'),

      paymentTerms: requiredText('Payment terms'),

      deliveryTerms: requiredText('Delivery terms'),

      gstApplicability: z
        .string()
        .trim()
        .max(150, 'GST applicability cannot exceed 150 characters')
        .default(''),

      additionalTerms: z
        .string()
        .trim()
        .max(500, 'Additional terms cannot exceed 500 characters')
        .default(''),
    }),
  })
  .superRefine((data, ctx) => {
    data.products.forEach((product, index) => {
      if (data.taxType === 'cgst_sgst') {
        if (product.cgst === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['products', index, 'cgst'],
            message: 'CGST is required',
          })
        }

        if (product.sgst === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['products', index, 'sgst'],
            message: 'SGST is required',
          })
        }
      }

      if (data.taxType === 'igst') {
        if (product.igst === undefined) {
          ctx.addIssue({
            code: 'custom',
            path: ['products', index, 'igst'],
            message: 'IGST is required',
          })
        }
      }
    })
  })
